<template>
  <div class="job-search row" v-if="canRead">
    <div class="advanced-search width-100" style="margin-bottom: 10px">
      <collapse v-model="activeNames">
        <collapse-item name="jobs-search" class="card col-md-12" style="border: none;">
          <template slot="title">
            <div class="card-header">
              <h4 class="card-title title-black" style="display: inline;">
                Jobs Search
              </h4>
            </div>
          </template>
          <div class="card-body form-group">
            <div class="row">
              <div class="col-md-6">
                <label>Search</label>
                <el-input type="search"
                       name="search"
                       clearable
                       placeholder="Search for a job name, number or description"
                       v-model="query.search"
                       @keyup.enter.native="searchSubmit"
                       v-validate="'minFields'"
                       data-vv-name="search"/>
                <span class="text-danger" v-show="errors.has('search')">
                  {{ mainSearchFieldErrorText }}
                </span>
              </div>
              <div class="col-md-6">
                <button @click="deleteSavedQueries" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> MANAGE </button>
                <label>Saved Searches</label>
                <v-select
                  name="savedQueryName"
                  v-model="selectedSavedQueryName"
                  placeholder="Select from previously saved searches"
                  label="label"
                  value="value"
                  @input="setSelectedSavedQuery"
                  class="inline"
                  :options="savedQueriesNamesList()">
                </v-select>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <label>Name
                  <el-tooltip class="item" effect="dark" content="One or more departments must be selected to load a list of available jobs." placement="top-start">
                    <img class="icon-help" src="static/img/icon-help.png"/>
                  </el-tooltip>
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <v-select
                  v-model="selectedJobName"
                  @input="setJobName"
                  placeholder="Job Name"
                  value="value"
                  label="label"
                  v-validate="'minFields'"
                  data-vv-name="search"
                  :options="jobNames">
                </v-select>
              </div>
              <div class="col-md-3">
                <label>Departments
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-select multiple class="select"
                           size="large"
                           placeholder="Department(s)"
                           v-model="query.departmentIds"
                           v-validate="'minFields'"
                           data-vv-name="search"
                           @input="verifyJobName">
                  <el-option v-for="option in allDepartments"
                             class="select"
                             :value="option.value"
                             :label="option.label"
                             :key="option.label">
                  </el-option>
                </el-select>
              </div>
              <div class="col-md-3">
                <label>Coordinator
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-autocomplete
                  style="width:100%"
                  size="large"
                  name="coordinatorId"
                  ref="coordinatorId"
                  class="inline-input"
                  clearable
                  v-model="selectedCoordinator"
                  :fetch-suggestions="findCoordinators"
                  :trigger-on-focus="false"
                  value-key="name"
                  @blur="verifyCoordinatorAutocompleteSelected"
                  placeholder="Coordinator"
                  v-validate="'minFields'"
                  data-vv-name="search"
                  @select="setCoordinator">
                </el-autocomplete>
                <small class="text-danger" v-show="showInvalidAutocompleteWarning('coordinatorId')">
                  {{ fieldNotValidError('Coordinator') }}
                </small>
              </div>
            </div>
            <div class="row">
              <div class="col-md-3">
                <label>Client
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-autocomplete
                  style="width:100%"
                  class="inline-input"
                  clearable
                  name="client"
                  ref="clientId"
                  :debounce="100"
                  v-model="selectedClient"
                  :fetch-suggestions="findClients"
                  :trigger-on-focus="false"
                  value-key="name"
                  @blur="verifyClientAutocompleteSelected"
                  placeholder="Client"
                  v-validate="'minFields'"
                  data-vv-name="client"
                  @select="setClient">
                </el-autocomplete>
                <small class="text-danger" v-show="showInvalidAutocompleteWarning('clientId')">
                  {{ fieldNotValidError('Client') }}
                </small>
              </div>
              <div class="col-md-3">
                <label>Client Contact
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-autocomplete
                    style="width:100%"
                    class="inline-input"
                    clearable
                    name="client"
                    ref="clientContactId"
                    :debounce="100"
                    v-model="selectedClientContact"
                    :fetch-suggestions="findClientContacts"
                    :trigger-on-focus="false"
                    value-key="name"
                    @blur="verifyClientContactAutocompleteSelected"
                    placeholder="Client Contact"
                    v-validate="'minFields'"
                    data-vv-name="clientContact"
                    @select="setClientContact">
                </el-autocomplete>
                <small class="text-danger" v-show="showInvalidAutocompleteWarning('clientContactId')">
                  {{ fieldNotValidError('Client Contact') }}
                </small>
              </div>
              <div class="col-md-3">
                <label>Builder
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-autocomplete
                  style="width:100%"
                  class="inline-input"
                  clearable
                  name="builder"
                  ref="builderId"
                  :debounce="100"
                  v-model="selectedBuilder"
                  :fetch-suggestions="findBuilders"
                  :trigger-on-focus="false"
                  value-key="name"
                  @blur="verifyBuilderAutocompleteSelected"
                  placeholder="Builder"
                  v-validate="'minFields'"
                  data-vv-name="builder"
                  @select="setBuilder">
                </el-autocomplete>
                <small class="text-danger" v-show="showInvalidAutocompleteWarning('builderId')">
                  {{ fieldNotValidError('Builder') }}
                </small>
              </div>
              <div class="col-md-3">
                <label>Builder Contact
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-autocomplete
                    style="width:100%"
                    class="inline-input"
                    clearable
                    name="builderContact"
                    ref="builderContactId"
                    :debounce="100"
                    v-model="selectedBuilderContact"
                    :fetch-suggestions="findBuilderContacts"
                    :trigger-on-focus="false"
                    value-key="name"
                    @blur="verifyBuilderContactAutocompleteSelected"
                    placeholder="Builder Contact"
                    v-validate="'minFields'"
                    data-vv-name="builderContact"
                    @select="setBuilderContact">
                </el-autocomplete>
                <small class="text-danger" v-show="showInvalidAutocompleteWarning('builderContactId')">
                  {{ fieldNotValidError('Builder Contact') }}
                </small>
              </div>
            </div>
            <div class="row">
              <div class="col-md-3">
                <label>Number
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-input type="search"
                          name="number"
                          placeholder="Job Number"
                          v-model="query.number"
                          clearable
                          @keyup.enter.native="searchSubmit"
                          v-validate="'minFields'"
                          data-vv-name="search"/>
              </div>
              <div class="col-md-3">
                <label>Permit Number
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-input type="search"
                          name="permitNumber"
                          placeholder="Permit Number"
                          v-model="query.permitNumber"
                          v-validate="'minFields'"
                          data-vv-name="search"
                          clearable/>
              </div>
              <div class="col-md-3">
                <label>Plan Name
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-autocomplete
                    style="width:100%"
                    class="inline-input"
                    clearable
                    :debounce="200"
                    v-model="selectedPlanName"
                    name="planNameId"
                    ref="planNameId"
                    :fetch-suggestions="findPlanNames"
                    :trigger-on-focus="false"
                    value-key="plan"
                    @focus="verifyBuilderSelected"
                    @blur="verifyPlanNameAutocompleteSelected"
                    placeholder="Plan Name"
                    v-validate="'minFields'"
                    data-vv-name="planName"
                    @select="setPlanName">
                </el-autocomplete>
                <small class="text-danger" v-show="showInvalidAutocompleteWarning('planNameId')">
                  {{ fieldNotValidError('Plan Name') }}
                </small>
              </div>
              <div class="col-md-3">
                <label>Community
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-autocomplete
                    style="width:100%"
                    class="inline-input"
                    clearable
                    name="community"
                    ref="communityId"
                    v-model="selectedCommunity"
                    :fetch-suggestions="findCommunities"
                    :trigger-on-focus="false"
                    value-key="name"
                    placeholder="Community"
                    @blur="verifyCommunityAutocompleteSelected"
                    v-validate="'minFields'"
                    data-vv-name="community"
                    @select="setCommunity">
                </el-autocomplete>
                <small class="text-danger" v-show="showInvalidAutocompleteWarning('communityId')">
                  {{ fieldNotValidError('Community') }}
                </small>
              </div>
            </div>
            <div class="row">
              <div class="col-md-3">
                <label>Status
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-select multiple class="select width-100"
                           size="large"
                           placeholder="Status"
                           clearable
                           v-validate="'minFields'"
                           data-vv-name="search"
                           v-model="query.status">
                  <el-option v-for="option in statuses"
                             class="select"
                             :value="option.value"
                             :label="option.label"
                             :key="option.label">
                  </el-option>
                </el-select>
              </div>
              <div class="col-md-3">
                <label>Priority
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-select class="select width-100"
                           size="large"
                           placeholder="Priority"
                           clearable
                           v-validate="'minFields'"
                           data-vv-name="search"
                           v-model="query.priority">
                  <el-option v-for="option in priorities"
                             class="select"
                             :value="option.value"
                             :label="option.label"
                             :key="option.label">
                  </el-option>
                </el-select>
              </div>
              <div class="col-md-3">
                <label>Date Received
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-date-picker
                  v-model="query.dateReceived"
                  type="daterange"
                  align="right"
                  unlink-panels
                  range-separator="-"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  :format="constants.DATE.FORMAT.DISPLAY"
                  :value-format="constants.DATE.FORMAT.VALUE"
                  :picker-options="datePickerConfig.dateRange"
                  v-validate="'minFields'"
                  data-vv-name="search"
                  placeholder="Date Received">
                </el-date-picker>
              </div>
              <div class="col-md-3">
                <label>Target Completion Date
                  <span class="text-danger" v-show="errors.has('search')">
                    {{ otherSearchFieldErrorText }}
                  </span>
                </label>
                <el-date-picker
                  v-model="query.targetCompletionDate"
                  type="daterange"
                  align="right"
                  unlink-panels
                  range-separator="-"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  :format="constants.DATE.FORMAT.DISPLAY"
                  :value-format="constants.DATE.FORMAT.VALUE"
                  :picker-options="datePickerConfig.dateRange"
                  v-validate="'minFields'"
                  data-vv-name="search"
                  placeholder="Target Completion Date">
                </el-date-picker>
              </div>
            </div>
          </div><!-- card content -->
          <div class="card-footer" style="height: 50px;">
            <button type="button" class="btn btn-fill btn-info pull-right" @click="searchSubmit">
              Search
            </button>
            <button type="button" class="btn btn-fill btn-info btn-company-search btn-clear pull-right" @click="clearForm()">
              Clear
            </button>
            <p-checkbox v-model="saveQuery" style="padding: 5px 12px 0 0;" class="pull-right">
              Save Search
            </p-checkbox>
          </div>
        </collapse-item>
      </collapse>
    </div>
    <div class="card">
      <div class="card-body row">
        <div class="col-sm-12">
          <div class="pull-right" style="margin-bottom: 6px;">
            <a @click.prevent="printerFriendly" v-if="hasResults" href="#"><img src="static/img/icon-print.png" title="Print Jobs" class="m-left-12"/></a>
            <a @click.prevent="downloadExcel" v-if="hasResults" href="#"><img src="static/img/icon-excel.jpg" title="Export to Excel" class="m-left-12"/></a>
            <a href="#" @click.prevent="handleShowPreferencesModal"><img src="static/img/gear-icon.png" title="Configure Columns" class="m-left-12" style="width: 26px; height: 26px;"/></a>
          </div>
        </div>
        <div class="col-sm-12">
          <div v-if="loading" style="text-align: center;">
            <img src="static/img/loading.gif"/>
          </div>
          <el-table class="table-striped wrap-whole-words indexed-table-column-width"
                    v-else
                    :data="sortedTableData"
                    :key="tableKey"
                    border
                    :max-height="tableHeight(300)"
                    :header-cell-class-name="headerSortClass"
                    style="width: 100%">
             <el-table-column
              :width="indexColumnWidth"
              align="center"
              label="#"
              type="index" />
            <el-table-column v-for="column in filteredTableColumns"
              :min-width="column.minWidth"
              :key="column.label"
              :label="column.label">
              <template v-slot="props">
                <component :is="column.component" :data="lodash.get(props.row, column.prop)" />
              </template>
            </el-table-column>
            <el-table-column
              :width="actionColumnWidth"
              fixed="right"
              label="Actions">
              <template v-slot="props">
                <a @click.prevent="viewLog(props.row._id)" href="#"><img src="static/img/icon-log.png" title="Job Error Log" class="m-left-12"/></a>
                <a @click.prevent="viewBulkEditTasks(props.row._id)" href="#"><img src="static/img/icon-tasks-edit.png" title="Bulk Edit Tasks" class="m-left-12" /></a>
                <a @click.prevent="viewTasks(props.row._id)" href="#"><img src="static/img/icon-tasks.png" title="Job Tasks" class="m-left-12"/></a>
                <a @click.prevent="viewJobProgress(props.row)" href="#" v-if="props.row._source.processInstanceId"><img src="static/img/icon-bpmn.png" title="Job Progress & Tasks" class="m-left-12"/></a>
                <div class="relative" style="display: inline-block"><a @click.prevent="openComments(props.row)" href="#"><img :id="'_' + props.row.id" :src="iconColour(getCommentCount(getParentEntityIdHash(props.row)))" :ref="getParentEntityIdHash(props.row)" title="Job Comments" class="m-left-8" /><div class="button-count button-count-jobs" v-if="getCommentCount(getParentEntityIdHash(props.row)) > 0" >{{ getCommentCount(getParentEntityIdHash(props.row)) }}</div></a></div>
                <a @click.prevent="confirmDelete(props.row._source)" href="#"><img src="static/img/icon-delete.png" title="Delete Job" class="m-left-12" v-if="canEdit"/></a>
                <span v-if="!isJobCompleted(props.row._source)">
                  <a @click.prevent="confirmTerminate(props.row._source)" href="#" v-if="canTerminateProcessInstance"><img src="static/img/icon-bpmn-error.png" title="Terminate Process Instance, Delete Tasks, and Complete Job" class="m-left-8" /></a>
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="col-sm-6 pagination-info">
          <span class="category">Showing {{total}} entries</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import PProgress from 'src/components/UIComponents/Progress.vue'
import Constants from 'src/utilities/constants'
import {Collapse, CollapseItem} from 'element-ui'
import Permissions from 'src/utilities/permissions'
import {mapGetters} from 'vuex'
import {
  assemblePlanNameDisplay,
  assembleTableColumnSortProps,
  autocompleteReady,
  autocompleteTextButNoValue,
  autocompleteTextButNoValueMessage,
  filterAndSortTableColumns,
  getParentEntityIdProjectDepartmentJobHash,
  getTableHeight,
  jobNamesForSelect,
  maxResultsWarning,
  notifyError,
  notifySuccess,
  printModal,
  setIndexColumnWidth,
  sortNested,
  tableHeaderSortCssClass,
  verifyAutocompleteSelected
} from 'src/utilities/utils'
import {confirmDeleteJob} from 'src/utilities/jobHelper'
import {minFields} from 'src/utilities/validation'
import {sync} from 'vuex-pathify'
import {ObjectID} from 'bson'
import vSelect from 'vue-select'
import lodash from 'lodash'
import _ from 'lodash'
import 'vue-select/dist/vue-select.css'
import {NO_CONTENT, OK} from 'http-status-codes'
import datePickerConfig from 'src/components/UIComponents/DatePicker/datePickerConfig'
import tableColumns from '@/utilities/tableColumns'
import {ptracGet} from '@/utilities/ptracApi'

export default{
  components: {
    PProgress,
    vSelect,
    Collapse,
    CollapseItem
  },
  computed: {
    query: sync('jobs/search@query'),
    saveQuery: sync('jobs/search/saveQuery@value'),
    user: sync('security/user'),
    ...mapGetters({
      priorities: 'priorities/job',
      statuses: 'statuses/job',
      activeModals: 'modalTracker/activeModals',
      autocompleteClients: 'companies/autocompleteResultsClient',
      autocompleteClientContact: 'companies/autocompleteResultsClientContact',
      autocompleteBuilders: 'companies/autocompleteResultsBuilder',
      autocompleteBuilderContact: 'companies/autocompleteResultsBuilderContact',
      autocompletePlanNames: 'planNames/results',
      users: 'users/kvpsForSelect',
      allDepartments: 'departments/kvpsForSelect',
      tableData: 'jobs/search/results'
    }),
    sortedTableData() {
      return this.tableData.sort(sortNested(this.sortedProps))
    },
    filteredTableColumns() {
      return filterAndSortTableColumns(this.tableColumns, this.user, Constants.USER_PREFERENCE_TYPES.SEARCH_JOBS)
    },
    sortedProps() {
      return assembleTableColumnSortProps(this.filteredTableColumns)
    },
    total() {
      return this.tableData.length
    },
    canRead() {
      return this.$can('read', Permissions.JOBS)
    },
    canEdit() {
      return this.$can('edit', Permissions.JOBS)
    },
    canTerminateProcessInstance() {
      return this.$can('edit', Permissions.JOB_TERMINATE_PROCESS_INSTANCE)
    },
    hasResults() {
      return this.tableData.length > 0
    },
    actionColumnWidth() {
      if (this.$store.get('security/isInGroup', Constants.GROUPS.STANDARD_USERS)
        || this.$store.get('security/isInGroup', Constants.GROUPS.ADVANCED_USERS)) {
        return 245 // column width hack
      }
      if (this.$store.get('security/isInGroup', Constants.GROUPS.POWER_USERS)) {
        return 275 // column width hack
      }
      if (this.$store.get('security/isInGroup', Constants.GROUPS.ADMIN_USERS)) {
        return 300 // column width hack
      }
      return 300 // column width hack
    },
    jobNames() {
      const departmentIds = this.$store.get('jobs/search/query@departmentIds')
      if (departmentIds) {
        return jobNamesForSelect(this, departmentIds)
      }
      return jobNamesForSelect(this)
    },
    verifyBuilderIsSelected() {
      return !!this.$store.get('jobs/search@query.builderId');
    },
    indexColumnWidth() {
      return setIndexColumnWidth(this.tableData.length)
    }
  },
  data() {
    return {
      lodash,
      activeNames: ['jobs-search'],
      datePickerConfig,
      tableKey: 0,
      jobName: null,
      toDelete: null,
      constants: Constants,
      selectedClient: null,
      selectedClientContact: null,
      selectedBuilder: null,
      selectedBuilderContact: null,
      selectedCoordinator: null,
      selectedCommunity: null,
      selectedJobName: null,
      selectedPlanName: null,
      client: '',
      builder: '',
      boolean: [
        {
          value: 'true',
          label: 'True'
        },
        {
          value: 'false',
          label: 'False'
        }
      ],
      mainSearchFieldErrorText: Constants.MESSAGES.AT_LEAST_ONE_FIELD_REQUIRED,
      otherSearchFieldErrorText: '*',
      loading: false,
      selectedSavedQueryName: null,
      tableColumns: tableColumns.JOB_SEARCH
    }
  },
  methods: {
    savedQueriesNamesList() {
      return Object.keys(this.user.savedJobQueries).sort()
    },
    isJobCompleted(job) {
      return Constants.STATUSES.COMPLETED === job.status || Constants.STATUSES.CANCELLED === job.status
    },
    confirmTerminate(job) {
      this.$eventHub.$emit('show-modal-1', 'Terminate Camunda Process', job, '1000px')
    },
    verifyBuilderSelected() {
      if (!this.$store.get('jobs/search@query.builderId')) {
        this.$eventHub.$emit('show-modal-4', 'No Builder', null, '500px')
        return false
      }
      return true
    },
    showInvalidAutocompleteWarning(refAndPathName) {
      // $ref has to match prop in store
      return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`jobs/search@query.${refAndPathName}`))
    },
    fieldNotValidError(field) {
      return autocompleteTextButNoValueMessage(field)
    },
    headerSortClass({columnIndex}) {
      return tableHeaderSortCssClass(this.filteredTableColumns, columnIndex, 1)
    },
    async clearForm(clearFromSavedQueries = false) {
      this.$store.commit('jobs/search/resetQuery')
      if (!clearFromSavedQueries) {
        this.$store.commit('jobs/search/resetResults')
        this.selectedSavedQueryName = null
        this.saveQuery = false
      }
      this.selectedClient = null
      this.selectedClientContact = null
      this.selectedBuilder = null
      this.selectedBuilderContact = null
      this.selectedCommunity = null
      this.selectedCoordinator = null
      this.jobName = null
      this.selectedJobName = null
      this.selectedPlanName = null
      await this.$validator.reset()
    },
    async delete() {
      const response = await this.$store.dispatch('delete', {endpoint: 'jobs', id: this.toDelete})
      if (NO_CONTENT === response.status) {
        this.$store.commit('jobs/search/delete', this.toDelete)
        notifySuccess(this, 'Job Deleted Successfully')
        this.$eventHub.$emit('jobDeleted')
      } else {
        notifyError(this, 'Error Deleting Job')
      }
    },
    viewBulkEditTasks(jobId) {
      this.$eventHub.$emit('show-modal-2', 'Bulk Edit Tasks', {jobId}, '95%')
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    async findPlanNames(text, cb) {
      const builderId = this.$store.get('jobs/search/query@builderId')
      if (this.verifyBuilderIsSelected && autocompleteReady(text) && !!builderId) {
        this.$store.set('jobs/search/query@planNameId', null)
        const payload = {
          name: text,
          number: text,
          builderId: builderId
        }
        await this.$store.dispatch('planNames/fetchByNameOrNumberAndBuilderId', payload)
        const planNames = this.$store.getters['planNames/resultsDisplay']

        if (planNames && planNames.length > 0) {
          cb(planNames)
        } else {
          this.$refs.planNameId.$refs.suggestions.showPopper = false // closes autocomplete results

          const plan = {
            id: new ObjectID(),
            name: this.selectedPlanName,
            active: true
          }

          if (this.$store.get('jobs/search/query@builderId')) {
            plan.builderId = this.$store.get('jobs/search/query@builderId')
          }
          this.$eventHub.$emit('show-modal-3', 'No Plan Name Results Found', plan, '500px')
        }
      }
    },
    async searchSubmit() {
      if (await this.$validator.validateAll()) {
        this.loading = true

        if (this.saveQuery) {
          this.$eventHub.$emit('show-modal-2', 'Save Search', {type: 'Job', query: {...this.query}}, '500px')
        }

        const response = await this.$store.dispatch('jobs/search/fetchResults')
        this.loading = false
        if (response.hits.total.value === 0) {
          this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
        }
        if (response.hits.total.value > Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS) {
          maxResultsWarning(this, Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS)
        }
      }
    },
    async setCommunity(community) {
      if (community) {
        this.selectedCommunity = community.name
        await this.$store.set('jobs/search/query@communityId', community.id)
      }
    },
    async setCoordinator(coordinator) {
      if (coordinator) {
        this.selectedCoordinator = coordinator.name
        await this.$store.set('jobs/search/query@coordinatorId', coordinator.id)
      }
    },
    async setPlanName(planName) {
      if (planName) {
        this.selectedPlanName = planName.plan
        this.$store.set('jobs/search/query@planNameId', planName.id)
      }
    },
    async setPlanNameAfterCreate(planName) {
      if (planName) {
        this.selectedPlanName = assemblePlanNameDisplay(planName)
        this.$store.set('jobs/search/query@planNameId', planName.id)
      }
    },
    noPlanNameSelected() {
      this.selectedPlanName = null
    },
    confirmDelete(job) {
      confirmDeleteJob(this, job)
    },
    showConfirmDelete(job, message, showButton) {
      const payload = {
        message,
        entity: job,
        delete: this.delete,
        showButton
      }
      this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
    },
    downloadExcel() {
      this.$store.dispatch('jobs/search/resultsToExcel')
    },
    async findBuilders(text, cb) {
      this.$store.set('jobs/search/query@builderId', null)
      await this.findCompany(text, Constants.COMPANY.TYPES.BUILDER)
      cb(this.autocompleteBuilders)
    },
    async findBuilderContacts(text, cb) {
      this.$store.set('jobs/search/query@builderContactId', null)
      await this.findCompanyContacts(text, Constants.COMPANY.TYPES.BUILDER)
      cb(this.autocompleteBuilderContact)
    },
    async findClients(text, cb) {
      this.$store.set('jobs/search/query@clientId', null)
      await this.findCompany(text, Constants.COMPANY.TYPES.CLIENT)
      cb(this.autocompleteClients)
    },
    async findClientContacts(text, cb) {
      this.$store.set('jobs/search/query@clientContactId', null)
      await this.findCompanyContacts(text, Constants.COMPANY.TYPES.CLIENT)
      cb(this.autocompleteClientContact)
    },
    async findCommunities(text, cb) {
      if (autocompleteReady(text)) {
        this.$store.set('jobs/search/query@communityId', null)
        const response = await this.$store.dispatch('communities/fetchByNameStartsWith', text)
        if (OK === response.status) {
          cb(response.data)
        }
      }
    },
    findCompany(text, type) {
      if (text) {
        const payload = {
          type: type,
          name: text
        }
        this.$store.dispatch('companies/fetchByNameStartsWithAndType', payload)
      }
    },
    findCompanyContacts(text, type) {
      if (autocompleteReady(text, 2)) {
        const companyId = type === Constants.COMPANY.TYPES.CLIENT ? this.query.clientId
            : type === Constants.COMPANY.TYPES.BUILDER ? this.query.builderId
                : null

        if (text) {
          const payload = {
            type: type,
            name: text.trim(),
            companyId: companyId
          }
          this.$store.dispatch('companies/fetchContactsForAutocomplete', payload)
        }
      }
    },
    findCoordinators(text, cb) {
      this.$store.set('jobs/search/query@coordinatorId', null)
      cb(this.$store.getters['users/findByNameStartsWith'](text))
    },
    getError(fieldName) {
      return this.errors.first(fieldName)
    },
    resultsNoValidate() {
      setTimeout(() => {
        this.$store.dispatch('jobs/search/fetchResults')
      }, Constants.TIME.MILLIS.TENTH_SECOND)
    },
    handleShowPreferencesModal() {
      const payload = {
        title: 'Search Jobs',
        preferenceType: Constants.USER_PREFERENCE_TYPES.SEARCH_JOBS
      }
      this.$eventHub.$emit('show-modal-1', 'Search and Sort Preferences', payload, '900px')
    },
    maybeDisplayDepartment() {
      if (_.get(this.user, 'preferences.global.defaultDepartment') && _.isEmpty(this.$store.get('jobs/search/query@departmentIds'))) {
        this.$store.set('jobs/search/query@departmentIds', [this.user.preferences.global.defaultDepartment])
      }
    },
    iconColour(commentCount) {
      return commentCount > 0 ? 'static/img/comment-red.png' : 'static/img/comment-blue.png'
    },
    getCommentCount(parentEntityIdHash) {
      return this.$store.getters['comments/count'](parentEntityIdHash)
    },
    getParentEntityIdHash(job) {
      return getParentEntityIdProjectDepartmentJobHash(job._source.projectId, job._source.departmentId, job._id)
    },
    openComments(job) {
      const payload = {
        parentId: {
          projectId: job._source.projectId,
          departmentId: job._source.departmentId,
          jobId: job._id
        },
        subheading: `<strong>Job:</strong> ${job._source.name}`
      }
      this.$eventHub.$emit('show-modal-1', 'Comments', payload)
    },
    printerFriendly() {
      this.$eventHub.$emit('show-modal-1', 'Job Search Results', null, '90%')
      printModal('jobSearchResults')
    },
    async setSelectedSavedQuery() {
      await this.clearForm(true)
      const selectedSavedQuery = this.user.savedJobQueries[this.selectedSavedQueryName]

      if (selectedSavedQuery) {
        Object.entries(selectedSavedQuery).forEach((key) => {this.query[key[0]] = key[1]})
        this.selectedJobName = selectedSavedQuery.name
        if (selectedSavedQuery.coordinatorId) {
          this.selectedCoordinator = this.$store.get('users/displayName', selectedSavedQuery.coordinatorId)
        }
        if (selectedSavedQuery.clientId) {
          const clientResponse = await ptracGet(`companies/${selectedSavedQuery.clientId}`)
          if (OK === clientResponse.status) {
            this.selectedClient = clientResponse.data.name
          }
        }
        if (selectedSavedQuery.clientContactId) {
          const clientContactResponse = await ptracGet('companies/search/findOneByContactsId?contactId=' + selectedSavedQuery.clientContactId)
          if (OK === clientContactResponse.status) {
            const clientContact = _.find(clientContactResponse.data.contacts, {id: selectedSavedQuery.clientContactId})
            this.selectedClientContact = clientContact.name + ' - ' + clientContactResponse.data.name
          }
        }
        if (selectedSavedQuery.builderId) {
          const builderResponse = await ptracGet(`companies/${selectedSavedQuery.builderId}`)
          if (OK === builderResponse.status) {
            this.selectedBuilder = builderResponse.data.name
          }
        }
        if (selectedSavedQuery.builderContactId) {
          const builderContactResponse = await ptracGet('companies/search/findOneByContactsId?contactId=' + selectedSavedQuery.builderContactId)
          if (OK === builderContactResponse.status) {
            const builderContact = _.find(builderContactResponse.data.contacts, {id: selectedSavedQuery.builderContactId})
            this.selectedBuilderContact = builderContact.name + ' - ' + builderContactResponse.data.name
          }
        }
        if (selectedSavedQuery.communityId) {
          const communityResponse = await ptracGet(`communities/${selectedSavedQuery.communityId}`)
          if (OK === communityResponse.status) {
            this.selectedCommunity = communityResponse.data.name
          }
        }
        if (selectedSavedQuery.planNameId) {
          const planNameResponse = await ptracGet(`plan-names/${selectedSavedQuery.planNameId}`)
          if (OK === planNameResponse.status) {
            this.selectedPlanName = assemblePlanNameDisplay(planNameResponse.data)
          }
        }
      }
    },
    deleteSavedQueries() {
      this.$eventHub.$emit('show-modal-1', 'Manage Saved Searches', {type: 'Job'}, '40%')
    },
    setBuilder(builder) {
      if (builder) {
        this.$store.set('jobs/search/query@builderId', builder.id)
        this.$store.commit('companies/autocompleteResultsBuilder', [])
        this.$validator.reset()
      }
    },
    setBuilderContact(builderContact) {
      if (builderContact) {
        this.$store.set('jobs/search/query@builderContactId', builderContact.id)
        this.$store.commit('companies/autocompleteResultsBuilderContact', [])
        this.$validator.reset()
      }
    },
    setClient(client) {
      if (client) {
        this.$store.set('jobs/search/query@clientId', client.id)
        this.$store.commit('companies/autocompleteResultsClient', [])
        this.$validator.reset()
      }
    },
    setClientContact(clientContact) {
      if (clientContact) {
        this.$store.set('jobs/search/query@clientContactId', clientContact.id)
        this.$store.commit('companies/autocompleteResultsClientContact', [])
        this.$validator.reset()
      }
    },
    setJobName(jobName) {
      this.$store.set('jobs/search/query@name', null)
      if (jobName) {
        this.$store.set('jobs/search/query@name', jobName.label)
      }
    },
    verifyJobName() {
      const jobNamesList = this.jobNames.map(jobName => jobName.label);
      const selectedJobName = {...this.selectedJobName}

      if (!jobNamesList.includes(selectedJobName.label)) {
        this.selectedJobName = null
      }
    },
    verifyPlanNameAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'jobs/search@query.planNameId', 'plan name', this.selectedPlanName)
    },
    verifyClientAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'jobs/search@query.clientId', 'client', this.selectedClient)
    },
    verifyClientContactAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'jobs/search@query.clientContactId', 'contact', this.selectedClientContact)
    },
    verifyBuilderAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'jobs/search@query.builderId', 'builder', this.selectedBuilder)
    },
    verifyBuilderContactAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'jobs/search@query.builderContactId', 'contact', this.selectedBuilderContact)
    },
    verifyCoordinatorAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'jobs/search@query.coordinatorId', 'coordinator', this.selectedCoordinator)
    },
    verifyCommunityAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'jobs/search@query.communityId', 'community', this.selectedCommunity)
    },
    viewJobProgress(jobResult) {
      this.$eventHub.$emit('show-modal-1', 'Job Progress', jobResult._source, '80%')
    },
    viewLog(jobId) {
      this.$eventHub.$emit('show-modal-1', 'View Log', {entityType: 'Job', entityId: jobId}, '80%')
    },
    viewTasks(jobId) {
      this.$store.commit('tasks/search/resetQuery')
      this.$store.set('tasks/search/query@jobId', jobId)
      this.$store.dispatch('tasks/search/fetchSearchResults', 1)
      this.$router.push({path: '/tasks/search?reset=false&sort=ptrac'})
    },
    async updateJobSearchResult(job) {
      await this.$store.dispatch('jobs/search/updateSearchResult', job)
      this.$forceUpdate()
    },
    savedSearchClosed(queryName) {
      this.saveQuery = false
      if (queryName && _.includes(this.savedQueriesNamesList(), queryName)) {
        this.selectedSavedQueryName = queryName
      }
    }
  },
  async beforeCreate() {
    await this.$store.cache.dispatch('statuses/fetchByEntityType', 'Job')
    await this.$store.cache.dispatch('priorities/fetchByEntityType', 'Job')
    await this.$store.cache.dispatch('users/fetchAll')
    await this.$store.cache.dispatch('camunda/fetchAllPtracDeployments')
  },
  async mounted() {
    this.$eventHub.$on('planNameSaved', this.setPlanNameAfterCreate)
    this.$eventHub.$on('noPlanNameSelected', this.noPlanNameSelected)
    this.$eventHub.$on('jobSaved', this.updateJobSearchResult)
    this.$eventHub.$on('savedSearchClosed', this.savedSearchClosed)
    minFields('jobs')
    this.maybeDisplayDepartment()
  },
  beforeDestroy() {
    this.$eventHub.$off('planNameSaved')
    this.$eventHub.$off('noPlanNameSelected')
    this.$eventHub.$off('jobSaved')
    this.$eventHub.$off('savedSearchClosed')
  },
  watch: {
    filteredTableColumns: function() {
      this.tableKey++
    },
    selectedBuilder: {
      handler: function (builder) {
        if (!builder) {
          this.$store.set('jobs/search/query@builderId', null)
          this.selectedBuilder = null
          this.$validator.reset()
        }
      }
    },
    selectedBuilderContact: {
      handler: function (builderContact) {
        if (!builderContact) {
          this.$store.set('jobs/search/query@builderContactId', null)
          this.selectedBuilderContact = null
          this.$validator.reset()
        }
      }
    },
    selectedClient: {
      handler: function (client) {
        if (!client) {
          this.$store.set('jobs/search/query@clientId', null)
          this.selectedClient = null
          this.$validator.reset()
        }
      }
    },
    selectedClientContact: {
      handler: function (clientContact) {
        if (!clientContact) {
          this.$store.set('jobs/search/query@clientContactId', null)
          this.selectedClientContact = null
          this.$validator.reset()
        }
      }
    },
    selectedCoordinator: {
      handler: function (selectedCoordinator) {
        if (!selectedCoordinator) {
          this.$store.set('jobs/search/query@coordinatorId', null)
          this.selectedCoordinator = null
          this.$validator.reset()
        }
      }
    },
    selectedCommunity: {
      handler: function (selectedCommunity) {
        if (!selectedCommunity) {
          this.$store.set('jobs/search/query@communityId', null)
          this.selectedCommunity = null
          this.$validator.reset()
        }
      }
    },
    selectedPlanName: {
      handler: function (selectedPlanName) {
        if (!selectedPlanName) {
          this.$store.set('jobs/search/query@planNameId', null)
          this.$validator.reset()
        }
      }
    },
    'user.preferences.global.defaultDepartment': {
      handler: function (defaultUserDepartment) {
      // Lets us wait for user preferences to load before trying to set default department
        if (defaultUserDepartment) {
          this.maybeDisplayDepartment()
        }
      }
    }
  }
}
</script>
<style scoped>
  .row label {
    display: block;
  }
  .advanced-search .row {
    margin-bottom: 10px;
  }
  .card .card-footer .btn-clear {
    margin-right: 12px;
  }
  .btn-clear {
    margin-left: 12px;
  }
  .title-black {
    line-height: 35px;
    font-size: 24px;
    color: #252422;
  }
  .btn-white {
    background-color: white;
    border-radius: 35px;
  }
  .button-count-jobs {
    position: absolute;
    right: -10px;
    bottom: 12px;
    height: 12px;
  }
  .job-search >>> .tiny-label-button {
    padding: 1px 5px;
    margin-top: -1px;
    float: right;
  }
</style>
